import { Dimension } from '@/types/readiness';

export const readinessQuestions: Dimension[] = [
  {
    id: 1,
    title: "Data Inventory",
    questions: [
      {
        question: "What is the current state of your permitting data?",
        options: [
          "Mostly paper-based, with minimal digitization",
          "Digital documents (PDFs, scanned files) but not structured",
          "Mix of structured (databases) and unstructured (PDFs) digital data",
          "Primarily structured data, though with some gaps",
          "Fully structured data across most permit records"
        ]
      },
      {
        question: "How accessible are your building codes and zoning ordinances?",
        options: [
          "Paper-based or static PDFs only",
          "Digital documents with basic search",
          "Structured database but internal only",
          "API-accessible",
          "Machine-readable format with regular updates"
        ]
      },
      {
        question: "How would you describe your data integration capabilities?",
        options: [
          "Separate systems with manual data sharing",
          "Some system connections but mostly manual",
          "Partial automation between key systems",
          "Most systems integrated with automated data flow",
          "Full integration across all relevant systems"
        ]
      }
    ]
  },
  {
    id: 2,
    title: "System & IT Infrastructure",
    questions: [
      {
        question: "How would you characterize your current permitting system?",
        options: [
          "Legacy system with minimal flexibility (on-prem, limited vendor support)",
          "Modern off-the-shelf but minimal integration (some custom scripts)",
          "Cloud-based system with basic API endpoints",
          "Fully modern, cloud-native with well-documented APIs",
          "Advanced system featuring AI modules or predictive analytics"
        ]
      },
      {
        question: "What level of system integration exists between permitting and other departments?",
        options: [
          "No integration, manual data sharing",
          "Basic file sharing capabilities",
          "Some automated data exchange",
          "Comprehensive API integration",
          "Full real-time data sharing"
        ]
      },
      {
        question: "How would you describe your department's cybersecurity measures?",
        options: [
          "Basic citywide firewall + minimal threat monitoring",
          "Standard municipal security protocols",
          "Advanced security with regular audits",
          "Comprehensive security framework with AI/ML monitoring",
          "Zero-trust architecture with continuous monitoring"
        ]
      }
    ]
  },
  {
    id: 3,
    title: "Skills & People",
    questions: [
      {
        question: "What level of technical expertise exists within your permitting staff?",
        options: [
          "Basic computer skills (MS Office) only",
          "Comfortable with current digital systems",
          "Some experience with data analysis or automation tools",
          "Experience with advanced digital tools and automation",
          "AI/ML project experience"
        ]
      },
      {
        question: "How would you rate your department's change management capabilities?",
        options: [
          "Limited experience with technology changes",
          "Some experience but unstructured approach",
          "Established change management process",
          "Successful track record of technology adoption",
          "Proven innovation leadership"
        ]
      },
      {
        question: "Does your team include specialized technical roles or rely on external resources?",
        options: [
          "No dedicated technical staff or external IT support",
          "Basic in-house IT support only; minimal external consulting",
          "We have a systems administrator but no data analysts",
          "We have data analysts/technical specialists",
          "We either have or contract with AI/ML specialists"
        ]
      }
    ]
  },
  {
    id: 4,
    title: "Policy",
    questions: [
      {
        question: "Are there specific local or state regulations/policies around AI usage in permitting?",
        options: [
          "No specific AI policies",
          "General technology policies only",
          "Draft AI policies in development",
          "Established AI governance framework",
          "Comprehensive AI policies with regular updates"
        ]
      },
      {
        question: "How mature are your data governance policies (including compliance with public records and privacy laws)?",
        options: [
          "No formal data governance",
          "Basic data management, minimal privacy compliance",
          "Established data governance but not AI-specific",
          "Comprehensive data + AI policies",
          "Advanced data governance with ethical AI guidelines, updated regularly"
        ]
      }
    ]
  },
  {
    id: 5,
    title: "Leadership Support",
    questions: [
      {
        question: "How has leadership demonstrated support for AI initiatives?",
        options: [
          "No real awareness or priority given",
          "Expressed interest but no budget",
          "Created a pilot budget or formed a task force",
          "Active champion, resources allocated",
          "Strategic priority with mandated goals and ongoing funding"
        ]
      },
      {
        question: "What level of AI understanding exists in leadership?",
        options: [
          "Limited understanding",
          "Basic awareness of AI capabilities",
          "Good grasp of AI opportunities",
          "Strong understanding with clear vision",
          "Deep knowledge with strategic direction"
        ]
      }
    ]
  },
  {
    id: 6,
    title: "Public Buy-in",
    questions: [
      {
        question: "How would you characterize public attitude toward technology initiatives?",
        options: [
          "Skeptical or resistant",
          "Neutral but uninformed",
          "Generally supportive with some concerns",
          "Actively engaged and supportive",
          "Strong advocacy for innovation"
        ]
      },
      {
        question: "How do you engage with the public on technology changes?",
        options: [
          "Minimal communication or occasional press release",
          "Basic announcements on city website or local bulletin",
          "Periodic updates and feedback sessions",
          "Regular open forums or surveys for major tech changes",
          "Collaborative partnerships, co-design with community groups"
        ]
      }
    ]
  },
  {
    id: 7,
    title: "Process Maturity",
    questions: [
      {
        question: "How well documented are your permitting processes?",
        options: [
          "Minimal documentation",
          "Basic process documentation",
          "Detailed workflows documented",
          "Standardized processes with regular updates",
          "Comprehensive process library with continuous improvement"
        ]
      },
      {
        question: "How do you currently measure process performance and identify bottlenecks?",
        options: [
          "We don't formally measure performance",
          "Track only basic volume metrics",
          "Track core metrics (turnaround time, revision frequency) and occasionally act",
          "Comprehensive KPI tracking with improvement goals",
          "Data-driven continuous improvement with real-time analytics"
        ]
      }
    ]
  }
];
